import React, {forwardRef, memo, useCallback, useImperativeHandle, useMemo, useRef, useState} from 'react';
import BranchSelect from "@/components/BranchSelect";
import StatesMap from "@/components/Maps/StatesMap";
import {ICONS, NAVIGATOR_VIEW_TYPE} from "@/constants";
import CommonIcon from "@/components/common/Icon/Icon";
import styled from "styled-components";
import {getCssSizeValue, getHtmlFromRedactorField} from "@/utils/utils";
import {useNavigatorContext} from "@/components/BranchNavigator";
import GoogleMap from "@/components/Maps/GoogleMap";
import useCurrentBranchData from "@/hooks/useCurrentBranchData";
import Text from "@/components/common/Text";
import useScrollContext from "@/hooks/useScrollContext";
import SimpleIconButton from "@/components/common/SimpleIconButton";
import SimpleMap from "@/components/Maps/SimpleMap";
import {BREAKPOINTS} from "@/styles/themeSettings";

const Wrapper = styled.div`
	--margin-x: ${getCssSizeValue(32)};
	position: relative;
    border: solid 1px ${({theme}) => theme.colors.richBlack};
	border-left: none;
	height: 85svh;
	display: flex;
	flex-direction: column;
	padding-top: ${getCssSizeValue(52)};
	z-index: 2;
	top: 0;
	transform: translateY(-1px);
	background-color: ${({theme}) => theme.colors.whiteSmoke};
	&.textile-collection {
		height: 85svh;
		transform: translateY(0);
		border-top: 0;
		border-bottom: 0;
	}

	
	.branch-select{
		margin-left: var(--margin-x);
	}
	
	.branch-select, .desc{
        margin-bottom: ${getCssSizeValue(40)};
	}
`

const ViewSwitcher = styled.div`
	position: relative;
	display: flex;
	width: 100%;
	height: ${getCssSizeValue(60)};
	bottom: 0;
	//left: calc(var(--margin-x) * -1);
`

const Icon = styled(CommonIcon)`
    border-top: solid 1px ${({theme}) => theme.colors.richBlack};
	border-right: solid 1px ${({theme}) => theme.colors.richBlack};
    background-color: ${({theme}) => theme.colors.white};
    height: 100%;
	width: auto;
	padding: ${getCssSizeValue(16)};
	aspect-ratio: 1;
	transition: background-color var(--rgr-duration) var(--ease);
	cursor: pointer;
	
	&.active, &:hover{
		background-color: ${({theme}) => theme.colors.periwinkle};
	}
`

const ViewContainer = styled.div`
	//margin-top: auto;
	display: flex;
    width: 100%;
	height: 100%;
	align-items: center;
	
	&.${NAVIGATOR_VIEW_TYPE.MAP}{
		padding: 0 var(--margin-x);
	}
`

const Description = styled(Text)`
    padding: 0 var(--margin-x);
`

const BothMapsWrapper = styled.div`
	position: relative;
	display: flex;
	width: 100%;
	height: 100%;
    bottom: ${getCssSizeValue(20)};
	#simple-map{
		position: absolute;
		left: 0;
		bottom: 0;
        width: 20%;
		aspect-ratio: 1;
	}

    #states-map{
		bottom: 0;
		position: absolute;
		max-height: 100%;
        --active-color: ${({theme}) => theme.colors.periwinkle};
    }
	
    @media (min-width: ${({theme}) => theme.breakpoints[BREAKPOINTS.DESKTOP]}px) and (orientation: landscape) {
        #simple-map{
            width: 18%;
        }
    }
`

const SummaryWrapper = styled.div`
	width: 100%;
	height: 100%;
	display: flex;
	flex-direction: column;
	background-color: ${({theme}) => theme.colors.richBlue};
    padding: 
			0
			${getCssSizeValue(16)} ;
	justify-content: center;
	word-break: break-word;
`

const StyledIconButton = styled(SimpleIconButton)`
	position: absolute;
	right: 0;
	translate: 50% 0;
	> div {
        transition: transform var(--rgr-duration) var(--ease);
	}
`

function trimLongName(str, length=40) {
	if (str.length > length) {
		let trimStr = str.slice(0, length);
		if (trimStr.endsWith(" ")) {
			trimStr = trimStr.slice(0, -1);
		}
		return trimStr + "...";
	} else {
		return str;
	}
}
const Summary = ({ data }) => {
	const name = data?.fullName || data?.name || ''
	// const formattedName = trimLongName(name)
	return (
		<SummaryWrapper>
			<Text className="typo-borna-102 name" color="white">{name}</Text>
		</SummaryWrapper>
	)
}

const ViewManager = forwardRef(function ViewManager({ description: desc='', ...props}, fref){
	const WrapperRef = useRef()
	const { view, setView , currentUnit, container, isTextileCollection } = useNavigatorContext()
	const currentBranch = useCurrentBranchData()
	const scroll = useScrollContext()
	const description = getHtmlFromRedactorField(desc)
	const current = useMemo(() => {
		return currentUnit?.id ? currentUnit : currentBranch
	},[currentUnit, currentBranch])
	const [arrowRotation, setArrowRotation] = useState(0)


	const handleBranchClick = useCallback(() => {
		if(container) {
			scroll.scrollTo({
				top: container,
			})
		}
	},[container])

	const onDropDownToggle = useCallback((open) => {
		setArrowRotation(open ? 180 : 0)
	},[])


	useImperativeHandle(fref, () => WrapperRef.current, [])

	return (
		<Wrapper ref={WrapperRef} className={`col-desktop-4 ${isTextileCollection ? 'textile-collection' : ''}`}
		>
			<BranchSelect
				onChange={handleBranchClick}
				onDropDownToggle={onDropDownToggle}
				className={"branch-select"}
			/>
			<StyledIconButton
				bgColor="chilliRed"
				color="white"
				rotation={arrowRotation}
			/>
			<Description tag="div" dangerouslySetInnerHTML={description} className="desc typo-strawford typo-strawford-65" />
			<ViewContainer className={`${view}`}>
				{view === NAVIGATOR_VIEW_TYPE.MAP ? (
					<BothMapsWrapper>
						<StatesMap onBranchClick={handleBranchClick} />
						<SimpleMap onBranchClick={handleBranchClick} />
					</BothMapsWrapper>
				) : (
					<GoogleMap
						lat={current.geolocation.latitude}
						lng={current.geolocation.longitude}
						zoom={currentBranch.handle === 'global' ? 5 : 12}
						apiKey={process.env.NEXT_GOOGLE_MAP_KEY}
						markers={{
							lat: current.geolocation.latitude,
							lng: current.geolocation.longitude
						}}
					/>
				)}
			</ViewContainer>
			<ViewSwitcher>
				<Icon
					name={ICONS.MAP}
					onClick={() => setView(NAVIGATOR_VIEW_TYPE.MAP)}
					className={`${view === NAVIGATOR_VIEW_TYPE.MAP ? 'active' : ''}`}
				/>
				<Icon
					name={ICONS.PIN_MAP}
					onClick={() => setView(NAVIGATOR_VIEW_TYPE.PIN_MAP)}
					className={`${view === NAVIGATOR_VIEW_TYPE.PIN_MAP ? 'active' : ''}`}
				/>
				{view === NAVIGATOR_VIEW_TYPE.PIN_MAP && (
					<Summary data={current}/>
				)}
			</ViewSwitcher>
		</Wrapper>
	);
})

export default memo(ViewManager);
